// material ui
import {
  Button,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Tooltip,
  Typography,
} from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import DeleteIcon from '@material-ui/icons/Delete';
import Autocomplete from '@material-ui/lab/Autocomplete';
// lodash
import isEqual from 'lodash/isEqual';
// moment
import moment from 'moment';
import React, { JSX } from 'react';

// api
import {
  makeTargetingChangeDescription,
  makeTargetingChangeInfoHover,
} from '~/src/dash/api/console-api';
import { EBool } from '~/src/dash/api/types/bool';
// components
import ConfirmationDialogButton from '~/src/dash/components/confirmation-dialog-button';
import { CategoryMultiSelect } from '~/src/dash/components/dropdowns/category-multiselect';
import { FocusedTextfield } from '~/src/dash/components/focused-textfield';
import { LogicToggle } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/LogicToggle';
import { fontColorsDefault } from '~/src/dash/components/layout/common';
import { MaterialSearchDropdownMulti } from '~/src/dash/components/material-search-dropdown-multi';
import { ModelSecondaryButton } from '~/src/dash/components/model-secondary-button';
import { fontSizing, Text, Textarea, TextButton } from '~/src/dash/publicaUI';
import { IListItem } from '~/src/dash/types/menuList';
// other
import { IWithStylesProps } from '~/src/dash/types/props';
// utils
import { CloneType } from '~/src/dash/utils/bulk-update';
import { publicaColors } from '~/src/dash/utils/colors';
import { decorate } from '~/src/dash/utils/decorator';
import { formatForClassname } from '~/src/dash/utils/format';
import { noopVoid } from '~/src/dash/utils/noop';
import { ELogicalOperator } from '~/src/dash/utils/options';
import { removeSpaces } from '~/src/dash/utils/string';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { ct, isLanguageOversized } from '~/src/locales/common';
import i18next from 'i18next';

// If additional render types are needed for the IInputRow, add them here and create a
// render function at the bottom of multi-accordion-panel.tsx
type RenderType =
  | ''
  | 'custom'
  | 'Textfield'
  | 'TextfieldAndSwitch'
  | 'CheckboxAndInfo'
  | 'NumberTextfield'
  | 'MaterialDropdown'
  | 'SimpleMultiSelect'
  | 'TextfieldAndInclude'
  | 'TextfieldAndIncludeAndLogic'
  | 'TextfieldAndIncludeV2'
  | 'TextfieldAndIncludeV2AndLogic'
  | 'SimpleSingleSelect'
  | 'SimpleSwitch'
  | 'TextfieldAndDoubleSwitch'
  | 'DoubleSwitch'
  | 'NumberTextfieldSmall'
  | 'AutocompleteAndIncludeRow'
  | 'SelectTextfieldRemove'
  | 'AddRowModelSecondaryButton';

export function makeInventoryRouteTargetingChangeDescription(isAllowing: boolean): string {
  if (isAllowing) {
    return ct('InventoryRouteTargetingChangeIncludeToExcludeDescription');
  }
  return ct('InventoryRouteTargetingChangeExcludeToIncludeDescription');
}

export function makeInventoryRouteTargetingChangeInfoHover(isAllowing: boolean): string {
  if (isAllowing) {
    return ct('InventoryRouteTargetingChangeIncludedTooltip');
  }
  return ct('InventoryRouteTargetingChangeExcludedTooltip');
}

export interface IInputRow {
  // ------- general row props ------- //
  // the type of render (checkbox, text field, custom, etc)
  renderType: RenderType;
  // custom render function for 'custom' renderType
  customRender?: (props: IInputRow, disableEdit: boolean) => any;
  // tooltip text (for textfields or tooltips)
  tooltipText?: string | JSX.Element;
  // helper text (for textfields)
  helperText?: string;
  // default label for simple multi select
  defaultLabel?: string;
  // placeholder text for a textfield
  placeholderText?: string;
  // items for the <CategoryMultiSelect> in 'SimpleMultiSelect'
  simpleSelectItems?: any[];
  // prop for 'MaterialDropdown' to show "All Selected" if no items are selected
  isNoneSelectedAllSelected?: boolean;
  // enables "small row" where the middle grids that typically contain the empty grid and flag are disabled if they are not needed / need to save space
  enableSmallRow?: boolean;
  // sets a max width for the "main" element of the row, applies if the row has a single main component on the right
  maxWidth?: number;
  // index number. use if you need to target an element in an array. eg: deleting a row item
  idx?: number;
  // button props
  hasButton?: boolean;
  buttonOnClick?: () => void;
  buttonText?: string;
  // isSubRow currently only for SimpleSingleSelect and NumberTextfieldRow
  isSubRow?: boolean;

  // ------- item 1 props ------- //
  // display title for the row
  title1: string;
  // if given, overrides the title in material dropdown and sets the width to 400px
  materialDropdownTitle?: string;
  materialDropdownTitleOverride?: string;
  // prop for 'MaterialDropdown'. must set to true to show ID prefix in title
  materialDropdownShowPrefixID?: boolean;
  // prop for 'MaterialDropdown', allow user to restrict single select when false.
  materialDropdownEnableMultiSelect?: boolean;
  // matches the name in state in the parent component for the handleChange function
  field1?: string;
  // value of item 1
  value1?: any;
  // handles change in the parent component for this row's item 1 field
  handleChange1?: (value: any) => void;
  // handles click for button components
  handleClick1?: (value: any) => void;
  // disabled for this field
  disabled1?: boolean;
  // checks if the value is default or "custom" / "active"
  isActiveValue1: () => boolean;
  // gets the tag to be displayed at the top of the Accordion Child
  // ex. 8 Channels vs All Channels or empty string
  // empty strings will not be displayed
  getActiveTag1: (active: boolean) => string;
  // handles "accept" of a 'MaterialDropdown' row
  handleAccept1?: (value: any) => void;
  // info display in front of textinput box
  prefixInfo?: string;
  // disabled Chip values for 'AutocompleteAndIncludeRow' row
  disabledvalues1?: string[];
  // disable select All option
  disableSelectAll?: boolean;

  quickFilterOptions?: any;

  // ------- optional item 2 props ------- //

  // handles change in the parent component for this row's item field (if necessary)
  // display title for the row
  title2?: string;
  // value of item 2
  value2?: any;
  // handles change in the parent component for this row's #1 field
  handleChange2?: (value?: any) => void;
  // disabled for field 2
  disabled2?: boolean;
  // shows an static icon without confirmation modal
  showStaticTargetingIcon?: boolean;

  // ------- optional item 3 props ------- //

  // handles change in the parent component for this row's item field (if necessary)
  // display title for the row
  title3?: string;
  // value of item 3
  value3?: any;
  // handles change in the parent component for this row's #3 field
  handleChange3?: (value?: any) => void;

  // disableEdit
  disableEdit?: boolean;
  error?: boolean;

  // start and end adornment for textfield
  startAdornment?: any;
  endAdornment?: any;

  showModalButton?: boolean;
  showModalButtonUnderDropdown?: boolean;
  modalButton?: JSX.Element;

  // ------- optional icon props ------- //

  // specifies name of optional icon
  optionalIcon?: string;
  // tooltip information for icon
  optionalIconInfo?: string;

  // resource for cypress test class labels
  resource?: string;

  // Page specific
  isInventoryRoutingPage?: boolean;

  // ------- bulk edit/clone bidder props ------- //

  // add "marked" for bulk editing/cloneBidder
  markedEnabled?: boolean;
  marked?: boolean;
  handleChangeMarked?: () => void;
  handleDisableMarked?: (key: string) => void;
  updateCloneType?: (key: string, type: CloneType) => void;

  cloneEnabled?: boolean;
  disableCheck?: boolean;
  compareValues?: boolean;

  defaultValue1?: any;
  clonedValue1?: any;

  defaultValue2?: any;
  clonedValue2?: any;

  defaultValue3?: any;
  clonedValue3?: any;

  // custom styling
  // SimpleSwitchRow
  // true will create enough spacing for short text in simple switch to fit on one line
  singleLineSwitchText?: boolean;

  // hide dropdown and only show modal button
  showDropdown?: boolean;
  // custom static tooltip
  staticTooltip?: JSX.Element;
}

interface IState {
  toUpdate: boolean;
}

export interface ITabRow {
  tabName: string;
  rows: IInputRow[];
}

export interface IInputRowProps extends IWithStylesProps {
  rowInfo: IInputRow;
  disableEdit: boolean;
  customClasses?: any;
}

export const InputRow = decorate(
  {
    styles: () => ({
      insideAccordionText: {
        fontSize: 14,
        fontWeight: 400,
        color: '$434343',
        maxWidth: 225,
      },
      insideAccordionTextBold: {
        fontSize: 14,
        fontWeight: 500,
        color: '$434343',
        maxWidth: 225,
      },
      insideAccordionTextfield: {
        fontSize: 12,
        fontWeight: 400,
        backgroundColor: '#F4F6FD',
        paddingLeft: 5,
        paddingTop: 8,
        paddingBottom: 8,
      },
      checkbox: {
        transform: 'scale(0.8)',
        padding: 3,
      },
      infoIconGrid: {
        paddingLeft: 9,
        marginTop: 2,
      },
      infoIconGridCheckbox: {
        paddingLeft: 9,
        marginTop: 5,
      },
      spanActive: {
        display: 'inline-flex',
        alignItems: 'center',
        width: 80,
        fontSize: 12,
        fontWeight: 500,
      },
      gridItemSmallMargin: {
        width: '100%',
        marginBottom: 5,
      },
      gridItemMidMargin: {
        width: '100%',
        marginBottom: 15,
      },
      gridItemBigMargin: {
        width: '100%',
        marginBottom: 25,
      },
    }),
  },
  class extends React.Component<IInputRowProps, IState> {
    constructor(props: IInputRowProps) {
      super(props);
      this.state = { toUpdate: true };
    }

    componentDidUpdate(prevProps: IInputRowProps) {
      const { rowInfo } = this.props;
      const {
        cloneEnabled,
        handleChangeMarked,
        marked,
        value1,
        defaultValue1,
        value2,
        defaultValue2,
        value3,
        defaultValue3,
        compareValues,
      } = rowInfo;

      // Handle auto marking for cloned Bidders
      // A diff check between the value and the default value should happen in one of two situations (assuming cloned is enabled & handleChangeMarked exists)
      // 1. value has updated since the last render
      // 2. it is the first time doing the diff (comes after loading cloned bidder info)
      if (
        (cloneEnabled && !isEqual(prevProps.rowInfo.value1, value1)) ||
        !isEqual(prevProps.rowInfo.value2, value2) ||
        !isEqual(prevProps.rowInfo.value3, value3) ||
        (this.state.toUpdate && compareValues)
      ) {
        // if row is marked, then to unmark, the values all need to be default values
        if (handleChangeMarked && marked) {
          if (
            this.valueAndDefaultValueAreEqual(value1, defaultValue1) &&
            this.valueAndDefaultValueAreEqual(value2, defaultValue2) &&
            this.valueAndDefaultValueAreEqual(value3, defaultValue3)
          ) {
            handleChangeMarked();
          }
          // if row is unmarked, then to mark, any of the values need to be not equal to default values
        } else if (handleChangeMarked && !marked) {
          if (
            !this.valueAndDefaultValueAreEqual(value1, defaultValue1) ||
            !this.valueAndDefaultValueAreEqual(value2, defaultValue2) ||
            !this.valueAndDefaultValueAreEqual(value3, defaultValue3)
          ) {
            handleChangeMarked();
          }
        }
        // This just sets the toUpdate state to false
        // used to run the handle change marked on initial bidder load
        setTimeout(() => {
          this.setState({
            toUpdate: false,
          });
        }, 0);
      }
    }

    valueAndDefaultValueAreEqual(value: any, defaultValue: any) {
      if (value === undefined) {
        return true;
      }
      if (defaultValue === undefined) {
        return false;
      }
      // if the value is an object its a drop down, with IListItems
      if (typeof value === 'object' && !Array.isArray(value)) {
        // if object is a moment (date) return false
        if (moment.isMoment(value)) {
          return false;
        } else if (Object.values(value).every((ele: IListItem) => ele.selected === defaultValue)) {
          return true;
        }
        // deep equals two values
      } else if (isEqual(value, defaultValue)) {
        return true;
        // checks equals if its just between types
        // currently implemented for Frequency Cap's current discrepancy in typing
        // to be removed once typing is updated
      } else if (value.toString() === defaultValue.toString()) {
        return true;
      }

      return false;
    }

    render() {
      const { rowInfo, disableEdit, classes } = this.props;
      const { renderType, customRender } = rowInfo;
      switch (renderType) {
        case 'custom':
          return customRender ? (
            customRender(rowInfo, disableEdit)
          ) : (
            <Typography>Custom Render Needed</Typography>
          );
        case 'Textfield':
          return TextfieldRow(rowInfo, disableEdit, classes);
        case 'TextfieldAndSwitch':
          return TextfieldAndSwitchRow(rowInfo, disableEdit, classes);
        case 'TextfieldAndDoubleSwitch':
          return TextfieldAndDoubleSwitchRow(rowInfo, disableEdit, classes);
        case 'DoubleSwitch':
          return DoubleSwitchRow(rowInfo, disableEdit, classes);
        case 'CheckboxAndInfo':
          return CheckboxAndInfoRow(rowInfo, disableEdit, classes);
        case 'NumberTextfield':
          return NumberTextfieldRow(rowInfo, disableEdit, classes);
        case 'NumberTextfieldSmall':
          return NumberTextfieldSmallRow(rowInfo, disableEdit, classes);
        case 'MaterialDropdown':
          return MaterialDropdownRow(rowInfo, disableEdit, classes);
        case 'SimpleMultiSelect':
          return SimpleMultiSelectRow(rowInfo, disableEdit, classes);
        case 'TextfieldAndInclude':
          return TextfieldAndIncludeRow(rowInfo, disableEdit, classes);
        case 'TextfieldAndIncludeAndLogic':
          return TextfieldAndIncludeRowAndLogic(rowInfo, disableEdit, classes);
        case 'TextfieldAndIncludeV2':
          return TextfieldAndIncludeRowV2(rowInfo, disableEdit, classes);
        case 'TextfieldAndIncludeV2AndLogic':
          return TextfieldAndIncludeV2AndLogic(rowInfo, disableEdit, classes);
        case 'SimpleSingleSelect':
          return SimpleSingleSelectRow(rowInfo, disableEdit, classes);
        case 'SimpleSwitch':
          return SimpleSwitchRow(rowInfo, disableEdit, classes);
        case 'AutocompleteAndIncludeRow':
          return AutocompleteAndIncludeRow(rowInfo, disableEdit, classes);
        case 'SelectTextfieldRemove':
          return SelectTextfieldRemove(rowInfo, disableEdit, classes);
        case 'AddRowModelSecondaryButton':
          return AddRowModelSecondaryButton(rowInfo, disableEdit, classes);
        default:
          // no matching render type defined
          return <Typography>There was an issue loading this panel</Typography>;
      }
    }
  },
);

// |-------- CUSTOM RENDERS ------| //

export const TextfieldRow = (props: IInputRow, disableEdit: boolean, classes: any) => {
  const {
    // REQUIRED PARAMS (1 is textfield, 2 is switch)
    title1,
    field1,
    value1,
    handleChange1,
    tooltipText,
    isActiveValue1,
    prefixInfo,
    disabled1,
    // OPTIONAL PARAMS
    placeholderText,
    optionalIcon,
    optionalIconInfo,
    error,
    helperText,
    marked,
    markedEnabled,
    cloneEnabled,
    handleChangeMarked,
    disableCheck,
    enableSmallRow,
    maxWidth,
  } = props;
  return (
    <Grid item className={classes.gridItemSmallMargin}>
      <Grid container direction='row' justify='flex-start' alignItems='center' spacing={1}>
        {/* Title */}
        <Grid item xs={3}>
          <Grid container direction='row' justify='flex-start' alignItems='center' spacing={0}>
            {(!!markedEnabled || !!cloneEnabled) && handleChangeMarked && (
              <Grid item>
                <Checkbox
                  className={classes.checkbox}
                  checked={marked}
                  onChange={handleChangeMarked}
                  disabled={disableCheck}
                />
              </Grid>
            )}
            <Grid item>
              <Typography
                className={
                  isActiveValue1() ? classes.insideAccordionTextBold : classes.insideAccordionText
                }
              >
                {title1}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        {/* Optional second icon */}
        {!enableSmallRow && (
          <Grid item xs={1}>
            {optionalIcon && optionalIconInfo && (
              <Tooltip title={optionalIconInfo}>
                <img src='/assets/shared/svg/strikethrough_flag.svg' />
              </Tooltip>
            )}
          </Grid>
        )}
        {/* Info show in front of the text input */}
        {!enableSmallRow && (
          <Grid item xs={1}>
            <Grid item xs={1} />
            <Grid item xs={9}>
              <Typography
                className={
                  isActiveValue1() ? classes.insideAccordionTextBold : classes.insideAccordionText
                }
              >
                {prefixInfo || ''}
              </Typography>
            </Grid>
            <Grid item xs={2} />
          </Grid>
        )}
        {/* Textfield */}
        <Grid item xs={7}>
          <Grid container alignItems='center' justify='flex-start' direction='row' spacing={1}>
            <Grid item xs={10} style={{ maxWidth: maxWidth ? maxWidth : '' }}>
              <FocusedTextfield
                fullWidth
                multiline
                placeholder={placeholderText || ''}
                label=''
                name={field1 || ''}
                value={value1}
                onChange={handleChange1 || noopVoid}
                disabled={disableEdit || disabled1}
                error={error}
                helperText={helperText || ''}
              />
            </Grid>
            {tooltipText && tooltipText !== '' && (
              <Grid item xs={2} className={classes.infoIconGrid}>
                <Tooltip title={tooltipText} placement='bottom-start'>
                  <div>
                    <IconV2 small name='info' color={publicaColors.mediumGray} />
                  </div>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const TextfieldAndSwitchRow = (props: IInputRow, disableEdit: boolean, classes: any) => {
  const {
    // REQUIRED PARAMS (1 is textfield, 2 is switch)
    title1,
    field1,
    value1,
    handleChange1,
    isActiveValue1,
    title2,
    value2,
    handleChange2,
    disabled1,
    // OPTIONAL PARAMS
    placeholderText,
    optionalIcon,
    optionalIconInfo,
    error,
    helperText,
    marked,
    markedEnabled,
    cloneEnabled,
    handleChangeMarked,
    disableCheck,
    enableSmallRow,
  } = props;
  return (
    <Grid item className={classes.gridItemSmallMargin}>
      <Grid container direction='row' justify='flex-start' alignItems='center' spacing={1}>
        {/* Title */}
        <Grid item xs={3}>
          <Grid container direction='row' justify='flex-start' alignItems='center' spacing={0}>
            {(!!markedEnabled || !!cloneEnabled) && handleChangeMarked && (
              <Grid item>
                <Checkbox
                  className={classes.checkbox}
                  checked={marked}
                  onChange={handleChangeMarked}
                  disabled={disableCheck}
                />
              </Grid>
            )}
            <Grid item>
              <Typography
                className={
                  isActiveValue1() ? classes.insideAccordionTextBold : classes.insideAccordionText
                }
              >
                {title1}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        {/* Optional second icon */}
        {!enableSmallRow && (
          <Grid item xs={1}>
            {optionalIcon && optionalIconInfo && (
              <Tooltip title={optionalIconInfo}>
                <img src='/assets/shared/svg/strikethrough_flag.svg' />
              </Tooltip>
            )}
          </Grid>
        )}
        {/* Empty grid for spacing */}
        {!enableSmallRow && <Grid item xs={1} />}
        <Grid item xs={4}>
          <FocusedTextfield
            fullWidth
            multiline
            placeholder={placeholderText || ''}
            label=''
            name={field1 || ''}
            value={value1}
            onChange={handleChange1 || noopVoid}
            disabled={disableEdit || disabled1}
            error={error}
            enableHelperText={!!helperText}
            defaultHelperText={helperText || ''}
          />
        </Grid>
        {/* Switch */}
        <Grid
          item
          xs={2}
          style={{ borderLeft: '1px solid gray', paddingLeft: '10px', marginLeft: '10px' }}
        >
          <Grid container direction='row' alignItems='center' justify='space-around'>
            <Grid item xs={7}>
              <Typography
                className={`${
                  isActiveValue1() ? classes.insideAccordionTextBold : classes.insideAccordionText
                } pb-test-switch`}
              >
                {title2}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Switch
                disabled={disableEdit || disabled1 || value1 === ''}
                checked={value2 === EBool.EBool_TRUE}
                name={field1}
                onChange={handleChange2}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const TextfieldAndDoubleSwitchRow = (
  props: IInputRow,
  disableEdit: boolean,
  classes: any,
) => {
  const {
    // REQUIRED PARAMS (1 is textfield, 2 is switch, 3 is switch)
    title1,
    field1,
    value1,
    handleChange1,
    isActiveValue1,
    title2,
    value2,
    handleChange2,
    title3,
    value3,
    handleChange3,
    disabled1,
    // OPTIONAL PARAMS
    placeholderText,
    optionalIcon,
    optionalIconInfo,
    error,
    helperText,
    marked,
    markedEnabled,
    cloneEnabled,
    handleChangeMarked,
    disableCheck,
    enableSmallRow,
  } = props;
  return (
    <Grid item className={classes.gridItemSmallMargin}>
      <Grid container direction='row' justify='flex-start' alignItems='center' spacing={1}>
        {/* Title */}
        <Grid item xs={3}>
          <Grid container direction='row' justify='flex-start' alignItems='center' spacing={0}>
            {(!!markedEnabled || !!cloneEnabled) && handleChangeMarked && (
              <Grid item>
                <Checkbox
                  className={classes.checkbox}
                  checked={marked}
                  onChange={handleChangeMarked}
                  disabled={disableCheck}
                />
              </Grid>
            )}
            <Grid item>
              <Typography
                className={
                  isActiveValue1() ? classes.insideAccordionTextBold : classes.insideAccordionText
                }
              >
                {title1}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        {/* Optional second icon */}
        {!enableSmallRow && (
          <Grid item xs={1}>
            {optionalIcon && optionalIconInfo && (
              <Tooltip title={optionalIconInfo}>
                <img src='/assets/shared/svg/strikethrough_flag.svg' />
              </Tooltip>
            )}
          </Grid>
        )}
        {/* Empty grid for spacing */}
        {!enableSmallRow && <Grid item xs={1} />}
        <Grid item xs={2}>
          <FocusedTextfield
            fullWidth
            multiline
            placeholder={placeholderText || ''}
            label=''
            name={field1 || ''}
            value={value1}
            onChange={handleChange1 || noopVoid}
            disabled={disableEdit || disabled1}
            error={error}
            helperText={helperText || ''}
          />
        </Grid>
        {/* Switch 1 */}
        <Grid
          item
          xs={2}
          style={{ borderLeft: '1px solid gray', paddingLeft: '10px', marginLeft: '10px' }}
        >
          <Grid container direction='row' alignItems='center' justify='space-around'>
            <Grid item xs={7}>
              <Typography
                className={
                  isActiveValue1() && value1 !== ''
                    ? classes.insideAccordionTextBold
                    : classes.insideAccordionText
                }
              >
                {title2}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Switch
                className={`cypress-inputrow-switch-add-if-empty-${formatForClassname(title1)}`}
                disabled={disableEdit || value1 === '' || disabled1}
                checked={value2 === EBool.EBool_TRUE}
                name={field1}
                onChange={handleChange2}
              />
            </Grid>
          </Grid>
        </Grid>
        {/* Switch 2 */}
        <Grid
          item
          xs={2}
          style={{ borderLeft: '1px solid gray', paddingLeft: '10px', marginLeft: '10px' }}
        >
          <Grid container direction='row' alignItems='center' justify='space-around'>
            <Grid item xs={7}>
              <Typography
                className={
                  isActiveValue1() && value1 === ''
                    ? classes.insideAccordionTextBold
                    : classes.insideAccordionText
                }
              >
                {title3}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Switch
                className={`cypress-inputrow-switch-remove-${formatForClassname(title1)}`}
                disabled={disableEdit || disabled1}
                checked={value3 === EBool.EBool_TRUE}
                name={field1}
                onChange={handleChange3}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const DoubleSwitchRow = (props: IInputRow, disableEdit: boolean, classes: any) => {
  const {
    // REQUIRED PARAMS (1 is first switch, 2 is second switch, title3 is row title)
    title1,
    value1,
    handleChange1,
    title2,
    value2,
    handleChange2,
    disabled1,
    title3,
  } = props;
  return (
    <Grid item className={classes.gridItemSmallMargin}>
      <Grid container direction='row' justify='space-around' alignItems='center' spacing={1}>
        {/* Title */}
        <Grid item xs={4}>
          <Grid container direction='row' justify='flex-start' alignItems='center'>
            <Grid item>
              <Typography className={classes.insideAccordionTextBold}>{title3}</Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={8}>
          <Grid container direction='row' justify='space-around' alignItems='center' spacing={1}>
            {/* Switch 1 */}
            <Grid
              item
              xs={5}
              style={{ borderLeft: '1px solid gray', paddingLeft: '10px', marginLeft: '10px' }}
            >
              <Grid container direction='row' alignItems='center' justify='space-evenly'>
                <Grid item xs={6}>
                  <Typography className={classes.insideAccordionText}>{title1}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Switch
                    disabled={disableEdit || value1 === '' || disabled1}
                    checked={value1 === EBool.EBool_TRUE}
                    name={title1}
                    onChange={handleChange1}
                  />
                </Grid>
              </Grid>
            </Grid>
            {/* Switch 2 */}
            <Grid
              item
              xs={6}
              style={{ borderLeft: '1px solid gray', paddingLeft: '10px', marginLeft: '10px' }}
            >
              <Grid container direction='row' alignItems='center' justify='space-evenly'>
                <Grid item xs={6}>
                  <Typography className={classes.insideAccordionText}>{title2}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Switch
                    disabled={disableEdit || disabled1}
                    checked={value2 === EBool.EBool_TRUE}
                    name={title2}
                    onChange={handleChange2}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const CheckboxAndInfoRow = (props: IInputRow, disableEdit: boolean, classes: any) => {
  const {
    // REQUIRED PARAMS
    title1,
    value1,
    handleChange1,
    isActiveValue1,
    disabled1,
    // OPTIONAL PARAMS
    tooltipText,
    optionalIcon,
    optionalIconInfo,
    marked,
    markedEnabled,
    cloneEnabled,
    handleChangeMarked,
    disableCheck,
    enableSmallRow,
    maxWidth,
  } = props;
  return (
    <Grid item className={classes.gridItemSmallMargin}>
      <Grid container direction='row' justify='flex-start' alignItems='center' spacing={1}>
        {/* Title and Info */}
        <Grid item xs={3}>
          <Grid container direction='row' justify='flex-start' alignItems='center' spacing={0}>
            {(!!markedEnabled || !!cloneEnabled) && handleChangeMarked && (
              <Grid item>
                <Checkbox
                  className={classes.checkbox}
                  checked={marked}
                  onChange={handleChangeMarked}
                  disabled={disableCheck}
                />
              </Grid>
            )}
            <Grid item>
              <Typography
                className={
                  isActiveValue1() ? classes.insideAccordionTextBold : classes.insideAccordionText
                }
              >
                {title1}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        {/* Optional second icon */}
        {!enableSmallRow && (
          <Grid item xs={1}>
            {optionalIcon && optionalIconInfo && (
              <Tooltip title={optionalIconInfo}>
                <img src='/assets/shared/svg/strikethrough_flag.svg' />
              </Tooltip>
            )}
          </Grid>
        )}
        {/* Empty grid for spacing */}
        {!enableSmallRow && <Grid item xs={1} />}
        {/* Checkbox */}
        <Grid item xs={6} style={{ maxWidth: maxWidth ? maxWidth : '' }}>
          <Grid container direction='row' justify='flex-start' alignItems='center' spacing={0}>
            <Grid item>
              <Checkbox
                className={`${classes.checkbox} pb-test-checkbox-${formatForClassname(title1)}`}
                checked={value1 === EBool.EBool_TRUE}
                onClick={handleChange1}
                disabled={disableEdit || disabled1}
              />
            </Grid>
            {tooltipText && tooltipText !== '' && (
              <Grid item className={classes.infoIconGridCheckbox}>
                <Tooltip title={tooltipText} placement='bottom-start'>
                  <div>
                    <IconV2 small name='info' color={publicaColors.mediumGray} />
                  </div>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const NumberTextfieldRow = (props: IInputRow, disableEdit: boolean, classes: any) => {
  const {
    // REQUIRED PARAMS
    title1,
    field1,
    value1,
    handleChange1,
    isActiveValue1,
    disabled1,
    // OPTIONAL PARAMS
    tooltipText,
    placeholderText,
    optionalIcon,
    optionalIconInfo,
    error,
    startAdornment,
    endAdornment,
    helperText,
    marked,
    markedEnabled,
    cloneEnabled,
    handleChangeMarked,
    disableCheck,
    enableSmallRow,
    maxWidth,
    hasButton,
    buttonOnClick,
    buttonText,
    isSubRow,
  } = props;
  return (
    <Grid item className={classes.gridItemSmallMargin}>
      <Grid container direction='row' justifyContent='flex-start' alignItems='center' spacing={1}>
        {/* Title */}
        <Grid item xs={3} justifyContent={!isSubRow ? 'flex-start' : 'flex-end'}>
          <Grid
            container
            direction='row'
            justifyContent={!isSubRow ? 'flex-start' : 'flex-end'}
            alignItems='center'
            spacing={0}
          >
            {(!!markedEnabled || !!cloneEnabled) && handleChangeMarked && (
              <Grid item>
                <Checkbox
                  className={classes.checkbox}
                  checked={marked}
                  onChange={handleChangeMarked}
                  disabled={disableCheck}
                />
              </Grid>
            )}
            <Grid item>
              <Typography
                className={
                  isActiveValue1() ? classes.insideAccordionTextBold : classes.insideAccordionText
                }
              >
                {title1}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        {/* Optional second icon */}
        {!enableSmallRow && (
          <Grid item xs={1}>
            {optionalIcon && optionalIconInfo && (
              <Tooltip title={optionalIconInfo}>
                <img src='/assets/shared/svg/strikethrough_flag.svg' />
              </Tooltip>
            )}
          </Grid>
        )}
        {/* Empty grid for spacing */}
        {!enableSmallRow && <Grid item xs={1} />}
        {/* Number textfield and info */}
        <Grid
          item
          xs={
            (tooltipText && tooltipText !== '') ||
            (isSubRow && (!tooltipText || tooltipText === ''))
              ? 5
              : 4
          }
        >
          <Grid
            container
            direction='row'
            justifyContent='flex-start'
            alignItems='center'
            spacing={0}
          >
            <Grid item style={{ maxWidth: maxWidth ? maxWidth : '' }}>
              <FocusedTextfield
                type='number'
                placeholder={placeholderText || ''}
                multiline
                label=''
                name={field1 || ''}
                value={value1}
                onChange={handleChange1 || noopVoid}
                error={error}
                startAdornment={startAdornment}
                endAdornment={endAdornment}
                enableHelperText={true}
                defaultHelperText={helperText || ''}
                disabled={disableEdit || disabled1}
              />
            </Grid>
            {tooltipText && tooltipText !== '' && (
              <Grid item className={classes.infoIconGrid}>
                <Tooltip title={tooltipText} placement='bottom-start'>
                  <div>
                    <IconV2 small name='info' color={publicaColors.mediumGray} />
                  </div>
                </Tooltip>
              </Grid>
            )}
            {/* Button */}
            {hasButton && buttonText !== '' && (
              <Grid
                item
                style={{
                  marginLeft: '8px',
                }}
              >
                <TextButton
                  onClick={() => {
                    if (buttonOnClick) {
                      buttonOnClick();
                    }
                  }}
                >
                  {buttonText}
                </TextButton>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const NumberTextfieldSmallRow = (props: IInputRow, disableEdit: boolean, classes: any) => {
  const {
    // REQUIRED PARAMS
    title1,
    field1,
    value1,
    handleChange1,
    isActiveValue1,
    disabled1,
    // OPTIONAL PARAMS
    tooltipText,
    placeholderText,
    error,
    startAdornment,
    endAdornment,
    helperText,
    marked,
    markedEnabled,
    cloneEnabled,
    handleChangeMarked,
    disableCheck,
    maxWidth,
  } = props;
  return (
    <Grid item className={classes.gridItemSmallMargin}>
      <Grid container direction='row' justify='flex-start' alignItems='center' spacing={1}>
        {/* Title */}
        <Grid item xs={2}>
          <Grid container direction='row' justify='flex-start' alignItems='center' spacing={0}>
            {(!!markedEnabled || !!cloneEnabled) && handleChangeMarked && (
              <Grid item>
                <Checkbox
                  className={classes.checkbox}
                  checked={marked}
                  onChange={handleChangeMarked}
                  disabled={disableCheck}
                />
              </Grid>
            )}
            <Grid item>
              <Typography
                className={
                  isActiveValue1() ? classes.insideAccordionTextBold : classes.insideAccordionText
                }
              >
                {title1}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        {/* Number textfield and info */}
        <Grid item xs={2}>
          <Grid container direction='row' justify='flex-start' alignItems='center' spacing={0}>
            <Grid item style={{ maxWidth: maxWidth ? maxWidth : '' }}>
              <FocusedTextfield
                type='number'
                placeholder={placeholderText || ''}
                multiline
                label=''
                name={field1 || ''}
                value={value1}
                onChange={handleChange1 || noopVoid}
                error={error}
                startAdornment={startAdornment}
                endAdornment={endAdornment}
                enableHelperText={true}
                defaultHelperText={helperText || ''}
                disabled={disableEdit || disabled1}
              />
            </Grid>
            {tooltipText && tooltipText !== '' && (
              <Grid item className={classes.infoIconGrid}>
                <Tooltip title={tooltipText} placement='bottom-start'>
                  <div>
                    <IconV2 small name='info' color={publicaColors.mediumGray} />
                  </div>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

// Add title to this array to show the number for dropdown
const showNumberRowTitle = ['Targeted Device Types'];

export const SimpleMultiSelectRow = (props: IInputRow, disableEdit: boolean, classes: any) => {
  const {
    // REQUIRED PARAMS (1 is multiSelect, 2 is inclusion / exclusion)
    title1,
    value1,
    field1,
    handleChange1,
    tooltipText,
    simpleSelectItems,
    isActiveValue1,
    disabled1,
    // OPTIONAL PARAMS for inclusion/exclusion
    value2,
    handleChange2,
    optionalIcon,
    optionalIconInfo,
    marked,
    markedEnabled,
    cloneEnabled,
    handleChangeMarked,
    disableCheck,
    enableSmallRow,
    maxWidth,
    defaultLabel,
  } = props;
  return (
    <Grid item className={classes.gridItemSmallMargin}>
      <Grid container direction='row' justify='flex-start' alignItems='center' spacing={1}>
        {/* Title */}
        <Grid item xs={3}>
          <Grid container direction='row' justify='flex-start' alignItems='center' spacing={0}>
            {(!!markedEnabled || !!cloneEnabled) && handleChangeMarked && (
              <Grid item>
                <Checkbox
                  className={classes.checkbox}
                  checked={marked}
                  onChange={handleChangeMarked}
                  disabled={disableCheck}
                />
              </Grid>
            )}
            <Grid item>
              <Typography
                className={
                  isActiveValue1() ? classes.insideAccordionTextBold : classes.insideAccordionText
                }
              >
                {title1}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        {/* Optional second icon */}
        {!enableSmallRow && (
          <Grid item xs={1}>
            {optionalIcon && optionalIconInfo && (
              <Tooltip title={optionalIconInfo}>
                <img src='/assets/shared/svg/strikethrough_flag.svg' />
              </Tooltip>
            )}
          </Grid>
        )}
        {/* OPTIONAL - targeting inclusion/exclusion */}
        {!enableSmallRow &&
          (handleChange2 ? (
            <Grid item xs={1}>
              <ConfirmationDialogButton
                title='Targeting Change'
                description={makeTargetingChangeDescription(value2)}
                triggerComponentInfo={makeTargetingChangeInfoHover(value2)}
                acceptButtonTitle='Confirm'
                acceptButtonCallBack={handleChange2}
                disabled={disableEdit || disabled1}
                refuseButtonTitle='Cancel'
              >
                <IconButton>
                  {value2 ? (
                    <IconV2 name='checkmark-plain' large color={publicaColors.green} />
                  ) : (
                    <IconV2 name='unavailable' large color={publicaColors.red} />
                  )}
                </IconButton>
              </ConfirmationDialogButton>
            </Grid>
          ) : (
            <Grid item xs={1} />
          ))}
        {/* Dropdown */}
        <Grid item xs={7} style={{ maxWidth: maxWidth ? maxWidth : '' }}>
          <CategoryMultiSelect
            label={defaultLabel ? defaultLabel : ''}
            helperText={tooltipText}
            name={field1}
            disabled={disableEdit || disabled1}
            items={simpleSelectItems}
            onChange={handleChange1}
            values={value1}
            style={{ maxWidth: 515 }}
            textfieldClass={classes.insideAccordionTextfield}
            selectFontSize={14}
            showNumber={showNumberRowTitle.includes(title1 || '')}
            key={title1}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export const MaterialDropdownRow = (props: IInputRow, disableEdit: boolean, classes: any) => {
  const {
    // REQUIRED PARAMS (1 is dropdown, 2 is inclusion / exclusion)
    title1,
    value1,
    handleChange1,
    handleAccept1,
    isActiveValue1,
    disabled1,
    // OPTIONAL PARAMS for inclusion/exclusion
    isInventoryRoutingPage,
    disabled2,
    value2,
    handleChange2,
    isNoneSelectedAllSelected,
    optionalIcon,
    optionalIconInfo,
    marked,
    markedEnabled,
    cloneEnabled,
    handleChangeMarked,
    disableCheck,
    enableSmallRow,
    maxWidth,
    showModalButton,
    showModalButtonUnderDropdown,
    showStaticTargetingIcon,
    modalButton,
    materialDropdownTitle,
    disableSelectAll,
    quickFilterOptions,
    materialDropdownTitleOverride,
    materialDropdownShowPrefixID,
    materialDropdownEnableMultiSelect,
    showDropdown,
  } = props;
  return (
    <Grid item className={classes.gridItemMidMargin}>
      <Grid container direction='row' justify='flex-start' alignItems='center' spacing={1}>
        {/* Title */}
        <Grid item xs={3}>
          <Grid container direction='row' justify='flex-start' alignItems='center' spacing={0}>
            {(!!markedEnabled || !!cloneEnabled) && handleChangeMarked && (
              <Grid item>
                <Checkbox
                  className={classes.checkbox}
                  checked={marked}
                  onChange={handleChangeMarked}
                  disabled={disableCheck}
                />
              </Grid>
            )}
            <Grid item>
              <Text
                bold={isActiveValue1()}
                small={!isLanguageOversized(i18next.language)}
                extraSmall={isLanguageOversized(i18next.language)}
              >
                {title1}
              </Text>
            </Grid>
          </Grid>
        </Grid>
        {/* Optional second icon */}
        {!enableSmallRow && (
          <Grid item xs={1}>
            {optionalIcon && optionalIconInfo && (
              <Tooltip title={optionalIconInfo}>
                <img src='/assets/shared/svg/strikethrough_flag.svg' />
              </Tooltip>
            )}
          </Grid>
        )}
        {/* OPTIONAL - targeting inclusion/exclusion */}
        {!enableSmallRow &&
          (handleChange2 ? (
            <Grid item xs={1}>
              <ConfirmationDialogButton
                title='Targeting Change'
                description={
                  isInventoryRoutingPage
                    ? makeInventoryRouteTargetingChangeDescription(value2)
                    : makeTargetingChangeDescription(value2)
                }
                triggerComponentInfo={
                  isInventoryRoutingPage
                    ? makeInventoryRouteTargetingChangeInfoHover(value2)
                    : makeTargetingChangeInfoHover(value2)
                }
                acceptButtonTitle={'Confirm'}
                acceptButtonCallBack={handleChange2}
                disabled={disableEdit || disabled1 || disabled2}
                refuseButtonTitle='Cancel'
              >
                <IconButton>
                  {value2 ? (
                    <IconV2 name='checkmark-plain' large color={publicaColors.green} />
                  ) : (
                    <IconV2 name='unavailable' large color={publicaColors.red} />
                  )}
                </IconButton>
              </ConfirmationDialogButton>
            </Grid>
          ) : showStaticTargetingIcon ? (
            <Grid item xs={1}>
              <IconButton>
                {value2 ? (
                  <IconV2 name='checkmark-plain' large color={publicaColors.green} />
                ) : (
                  <IconV2 name='unavailable' large color={publicaColors.red} />
                )}
              </IconButton>
            </Grid>
          ) : (
            <Grid item xs={1} />
          ))}
        {/* Dropdown */}
        <Grid item xs={7} style={{ flexDirection: 'row', maxWidth: maxWidth ? maxWidth : '' }}>
          <MaterialSearchDropdownMulti
            getValueFromSearchElement={(src: IListItem) => src.title}
            fontSize={
              isLanguageOversized(i18next.language) ? fontSizing.extraSmall : fontSizing.small
            }
            selectAll={disableSelectAll ? false : true}
            width={materialDropdownTitle ? '400px' : ''}
            menuTitle={materialDropdownTitle || title1 || ''}
            menuTitleOverride={materialDropdownTitleOverride}
            list={value1}
            disabled={disableEdit || disabled1}
            disabledTooltip={
              isInventoryRoutingPage ? 'Item already selected by another destination' : ''
            }
            onChange={handleChange1 || noopVoid}
            handleMenuAcceptClick={handleAccept1}
            isNoneSelectedAllSelected={isNoneSelectedAllSelected}
            showModalButton={showModalButton}
            showDropdown={showDropdown}
            modalButton={modalButton}
            quickFilterOptions={quickFilterOptions}
            showPrefixID={!!materialDropdownShowPrefixID}
            enableMultiSelect={materialDropdownEnableMultiSelect}
          />
        </Grid>
        {showModalButtonUnderDropdown && (
          <Grid container direction='row' justify='flex-start' alignItems='center' spacing={1}>
            <Grid item xs={5}></Grid>
            <Grid item xs={7} style={{ flexDirection: 'row', maxWidth: maxWidth ? maxWidth : '' }}>
              {modalButton}
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export const TextfieldAndIncludeRow = (props: IInputRow, disableEdit: boolean, classes: any) => {
  const {
    // REQUIRED PARAMS (1 is textfield, 2 is inclusion / exclusion)
    field1,
    title1,
    value1,
    handleChange1,
    value2,
    handleChange2,
    isActiveValue1,
    disabled1,
    // OPTIONAL PARAMS for inclusion/exclusion
    isInventoryRoutingPage,
    disabled2,
    tooltipText,
    placeholderText,
    optionalIcon,
    optionalIconInfo,
    error,
    helperText,
    marked,
    markedEnabled,
    cloneEnabled,
    handleChangeMarked,
    disableCheck,
    enableSmallRow,
    maxWidth,
  } = props;
  return (
    <Grid item className={classes.gridItemMidMargin}>
      <Grid container direction='row' justify='flex-start' alignItems='center' spacing={1}>
        {/* Title */}
        <Grid item xs={3}>
          <Grid container direction='row' justify='flex-start' alignItems='center' spacing={0}>
            {(!!markedEnabled || !!cloneEnabled) && handleChangeMarked && (
              <Grid item>
                <Checkbox
                  className={classes.checkbox}
                  checked={marked}
                  onChange={handleChangeMarked}
                  disabled={disableCheck}
                />
              </Grid>
            )}
            <Grid item>
              <Typography
                className={
                  isActiveValue1() ? classes.insideAccordionTextBold : classes.insideAccordionText
                }
              >
                {title1}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        {/* Optional second icon */}
        {!enableSmallRow && (
          <Grid item xs={1}>
            {optionalIcon && optionalIconInfo && (
              <Tooltip title={optionalIconInfo}>
                <img src='/assets/shared/svg/strikethrough_flag.svg' />
              </Tooltip>
            )}
          </Grid>
        )}
        {/* targeting inclusion/exclusion */}
        {!enableSmallRow && (
          <Grid item xs={1}>
            {handleChange2 && (
              <ConfirmationDialogButton
                title='Targeting Change'
                description={
                  isInventoryRoutingPage
                    ? makeInventoryRouteTargetingChangeDescription(value2)
                    : makeTargetingChangeDescription(value2)
                }
                triggerComponentInfo={
                  isInventoryRoutingPage
                    ? makeInventoryRouteTargetingChangeInfoHover(value2)
                    : makeTargetingChangeInfoHover(value2)
                }
                acceptButtonTitle='Confirm'
                acceptButtonCallBack={handleChange2}
                disabled={disableEdit || disabled1 || disabled2}
                refuseButtonTitle='Cancel'
              >
                <IconButton>
                  {value2 ? (
                    <IconV2 name='checkmark-plain' large color={publicaColors.green} />
                  ) : (
                    <IconV2 name='unavailable' large color={publicaColors.red} />
                  )}
                </IconButton>
              </ConfirmationDialogButton>
            )}
          </Grid>
        )}
        {/* Textfield and info */}
        <Grid item xs={7}>
          <Grid container alignItems='center' justify='flex-start' direction='row' spacing={1}>
            <Grid item xs={10} style={{ maxWidth: maxWidth ? maxWidth : '' }}>
              <FocusedTextfield
                fullWidth
                multiline
                placeholder={placeholderText || ''}
                label=''
                name={field1 || ''}
                value={value1}
                onChange={handleChange1 || noopVoid}
                disabled={disableEdit || disabled1}
                error={error}
                helperText={helperText || ''}
              />
            </Grid>
            {tooltipText && tooltipText !== '' && (
              <Grid item xs={2} className={classes.infoIconGrid}>
                <Tooltip interactive title={tooltipText} placeholder='right'>
                  <div>
                    <IconV2 small name='info' color={publicaColors.mediumGray} />
                  </div>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const TextfieldAndIncludeRowAndLogic = (
  props: IInputRow,
  disableEdit: boolean,
  classes: any,
) => {
  const {
    // REQUIRED PARAMS (1 is textfield, 2 is inclusion / exclusion)
    field1,
    title1,
    value1,
    handleChange1,
    value2,
    handleChange2,
    isActiveValue1,
    disabled1,
    value3,
    handleChange3,
    // OPTIONAL PARAMS for inclusion/exclusion
    isInventoryRoutingPage,
    disabled2,
    tooltipText,
    placeholderText,
    error,
    helperText,
    marked,
    markedEnabled,
    cloneEnabled,
    handleChangeMarked,
    disableCheck,
    enableSmallRow,
    maxWidth,
  } = props;

  const orTooltipMessage = `Enable OR logic between App Bundle and Site Pages; requests will reach this (bidder/line item) if any of the entered IDs and site pages match.`;
  const andTooltipMessage = `Enable AND logic between App Bundle and Site Pages; requests will reach this (bidder/line item) only if all of the entered IDs and site pages match.`;

  const renderAndOr = () => {
    return (
      <Grid item xs={1} className={classes.smallNegativeMargin} onClick={handleChange3}>
        <LogicToggle
          operator={value3}
          handleToggleOperator={() => {
            if (handleChange3) {
              handleChange3();
            }
          }}
          tooltip={value3 === ELogicalOperator.OR ? orTooltipMessage : andTooltipMessage}
        />
      </Grid>
    );
  };

  return (
    <Grid item className={classes.gridItemMidMargin}>
      <Grid container direction='row' justify='flex-start' alignItems='center' spacing={1}>
        {/* Title */}
        <Grid item xs={3}>
          <Grid container direction='row' justify='flex-start' alignItems='center' spacing={0}>
            {(!!markedEnabled || !!cloneEnabled) && handleChangeMarked && (
              <Grid item>
                <Checkbox
                  className={classes.checkbox}
                  checked={marked}
                  onChange={handleChangeMarked}
                  disabled={disableCheck}
                />
              </Grid>
            )}
            <Grid item>
              <Typography
                className={
                  isActiveValue1() ? classes.insideAccordionTextBold : classes.insideAccordionText
                }
              >
                {title1}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        {renderAndOr()}
        {/* targeting inclusion/exclusion */}
        {!enableSmallRow && (
          <Grid item xs={1}>
            {handleChange2 && (
              <ConfirmationDialogButton
                title='Targeting Change'
                description={
                  isInventoryRoutingPage
                    ? makeInventoryRouteTargetingChangeDescription(value2)
                    : makeTargetingChangeDescription(value2)
                }
                triggerComponentInfo={
                  isInventoryRoutingPage
                    ? makeInventoryRouteTargetingChangeInfoHover(value2)
                    : makeTargetingChangeInfoHover(value2)
                }
                acceptButtonTitle='Confirm'
                acceptButtonCallBack={handleChange2}
                disabled={disableEdit || disabled1 || disabled2}
                refuseButtonTitle='Cancel'
              >
                <IconButton>
                  {value2 ? (
                    <IconV2 name='checkmark-plain' large color={publicaColors.green} />
                  ) : (
                    <IconV2 name='unavailable' large color={publicaColors.red} />
                  )}
                </IconButton>
              </ConfirmationDialogButton>
            )}
          </Grid>
        )}
        {/* Textfield and info */}
        <Grid item xs={7}>
          <Grid container alignItems='center' justify='flex-start' direction='row' spacing={1}>
            <Grid item xs={10} style={{ maxWidth: maxWidth ? maxWidth : '' }}>
              <FocusedTextfield
                fullWidth
                multiline
                placeholder={placeholderText || ''}
                label=''
                name={field1 || ''}
                value={value1}
                onChange={handleChange1 || noopVoid}
                disabled={disableEdit || disabled1}
                error={error}
                helperText={helperText || ''}
              />
            </Grid>
            {tooltipText && tooltipText !== '' && (
              <Grid item xs={2} className={classes.infoIconGrid}>
                <Tooltip interactive title={tooltipText} placeholder='right'>
                  <div>
                    <IconV2 small name='info' color={publicaColors.mediumGray} />
                  </div>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const TextfieldAndIncludeRowV2 = (props: IInputRow, disableEdit: boolean, classes: any) => {
  const {
    // REQUIRED PARAMS (1 is textfield, 2 is inclusion / exclusion)
    title1,
    value1,
    handleChange1,
    value2,
    handleChange2,
    isActiveValue1,
    disabled1,
    // OPTIONAL PARAMS for inclusion/exclusion
    isInventoryRoutingPage,
    disabled2,
    tooltipText,
    placeholderText,
    optionalIcon,
    optionalIconInfo,
    marked,
    markedEnabled,
    cloneEnabled,
    handleChangeMarked,
    disableCheck,
    enableSmallRow,
    maxWidth,
  } = props;
  return (
    <Grid item className={classes.gridItemMidMargin}>
      <Grid container direction='row' justify='flex-start' alignItems='center' spacing={1}>
        {/* Title */}
        <Grid item xs={3}>
          <Grid container direction='row' justify='flex-start' alignItems='center' spacing={0}>
            {(!!markedEnabled || !!cloneEnabled) && handleChangeMarked && (
              <Grid item>
                <Checkbox
                  className={classes.checkbox}
                  checked={marked}
                  onChange={handleChangeMarked}
                  disabled={disableCheck}
                />
              </Grid>
            )}
            <Grid item>
              <Text
                bold={isActiveValue1()}
                small={!isLanguageOversized(i18next.language)}
                extraSmall={isLanguageOversized(i18next.language)}
              >
                {title1}
              </Text>
            </Grid>
          </Grid>
        </Grid>
        {/* Optional second icon */}
        {!enableSmallRow && (
          <Grid item xs={1}>
            {optionalIcon && optionalIconInfo && (
              <Tooltip title={optionalIconInfo}>
                <img src='/assets/shared/svg/strikethrough_flag.svg' />
              </Tooltip>
            )}
          </Grid>
        )}
        {/* targeting inclusion/exclusion */}
        {!enableSmallRow && (
          <Grid item xs={1}>
            {handleChange2 && (
              <ConfirmationDialogButton
                title='Targeting Change'
                description={
                  isInventoryRoutingPage
                    ? makeInventoryRouteTargetingChangeDescription(value2)
                    : makeTargetingChangeDescription(value2)
                }
                triggerComponentInfo={
                  isInventoryRoutingPage
                    ? makeInventoryRouteTargetingChangeInfoHover(value2)
                    : makeTargetingChangeInfoHover(value2)
                }
                acceptButtonTitle='Confirm'
                acceptButtonCallBack={handleChange2}
                disabled={disableEdit || disabled1 || disabled2}
                refuseButtonTitle='Cancel'
              >
                <IconButton>
                  {value2 ? (
                    <IconV2 name='checkmark-plain' large color={publicaColors.green} />
                  ) : (
                    <IconV2 name='unavailable' large color={publicaColors.red} />
                  )}
                </IconButton>
              </ConfirmationDialogButton>
            )}
          </Grid>
        )}
        {/* Textfield and info */}
        <Grid item xs={7}>
          <Grid container alignItems='center' justify='flex-start' direction='row' spacing={1}>
            <Grid item xs={10} style={{ maxWidth: maxWidth ? maxWidth : '' }}>
              <Textarea
                fullWidth
                msDelay={800}
                isDisabled={disableEdit || disabled1}
                placeholder={placeholderText || ''}
                initialValue={value1}
                handleOnChange={handleChange1 || noopVoid}
                size={isLanguageOversized(i18next.language) ? 'extraSmall' : 'small'}
              />
            </Grid>
            {tooltipText && (
              <Grid item xs={2} className={classes.infoIconGrid}>
                <Tooltip interactive title={tooltipText} placeholder='right'>
                  <div>
                    <IconV2 small name='info' color={publicaColors.mediumGray} />
                  </div>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const TextfieldAndIncludeV2AndLogic = (
  props: IInputRow,
  disableEdit: boolean,
  classes: any,
) => {
  const {
    // REQUIRED PARAMS (1 is textfield, 2 is inclusion / exclusion)
    title1,
    value1,
    handleChange1,
    value2,
    handleChange2,
    isActiveValue1,
    disabled1,
    value3,
    handleChange3,
    // OPTIONAL PARAMS for inclusion/exclusion
    isInventoryRoutingPage,
    disabled2,
    tooltipText,
    placeholderText,
    marked,
    markedEnabled,
    cloneEnabled,
    handleChangeMarked,
    disableCheck,
    enableSmallRow,
    maxWidth,
  } = props;

  const orTooltipMessage = `Enable OR logic between App Bundle and Site Pages; requests will reach this (bidder/line item) if any of the entered IDs and site pages match.`;
  const andTooltipMessage = `Enable AND logic between App Bundle and Site Pages; requests will reach this (bidder/line item) only if all of the entered IDs and site pages match.`;

  const renderAndOr = () => {
    return (
      <Grid item xs={1} className={classes.smallNegativeMargin} onClick={handleChange3}>
        <LogicToggle
          operator={value3}
          handleToggleOperator={() => {
            if (handleChange3) {
              handleChange3();
            }
          }}
          tooltip={value3 === ELogicalOperator.OR ? orTooltipMessage : andTooltipMessage}
        />
      </Grid>
    );
  };

  return (
    <Grid item className={classes.gridItemMidMargin}>
      <Grid container direction='row' justify='flex-start' alignItems='center' spacing={1}>
        {/* Title */}
        <Grid item xs={3}>
          <Grid container direction='row' justify='flex-start' alignItems='center' spacing={0}>
            {(!!markedEnabled || !!cloneEnabled) && handleChangeMarked && (
              <Grid item>
                <Checkbox
                  className={classes.checkbox}
                  checked={marked}
                  onChange={handleChangeMarked}
                  disabled={disableCheck}
                />
              </Grid>
            )}
            <Grid item>
              <Typography
                className={
                  isActiveValue1() ? classes.insideAccordionTextBold : classes.insideAccordionText
                }
              >
                {title1}
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        {renderAndOr()}

        {/* targeting inclusion/exclusion */}
        {!enableSmallRow && (
          <Grid item xs={1}>
            {handleChange2 && (
              <ConfirmationDialogButton
                title='Targeting Change'
                description={
                  isInventoryRoutingPage
                    ? makeInventoryRouteTargetingChangeDescription(value2)
                    : makeTargetingChangeDescription(value2)
                }
                triggerComponentInfo={
                  isInventoryRoutingPage
                    ? makeInventoryRouteTargetingChangeInfoHover(value2)
                    : makeTargetingChangeInfoHover(value2)
                }
                acceptButtonTitle='Confirm'
                acceptButtonCallBack={handleChange2}
                disabled={disableEdit || disabled1 || disabled2}
                refuseButtonTitle='Cancel'
              >
                <IconButton>
                  {value2 ? (
                    <IconV2 name='checkmark-plain' large color={publicaColors.green} />
                  ) : (
                    <IconV2 name='unavailable' large color={publicaColors.red} />
                  )}
                </IconButton>
              </ConfirmationDialogButton>
            )}
          </Grid>
        )}
        {/* Textfield and info */}
        <Grid item xs={7}>
          <Grid container alignItems='center' justify='flex-start' direction='row' spacing={1}>
            <Grid item xs={10} style={{ maxWidth: maxWidth ? maxWidth : '' }}>
              <Textarea
                fullWidth
                msDelay={800}
                isDisabled={disableEdit || disabled1}
                placeholder={placeholderText || ''}
                initialValue={value1}
                handleOnChange={handleChange1 || noopVoid}
              />
            </Grid>
            {tooltipText && (
              <Grid item xs={2} className={classes.infoIconGrid}>
                <Tooltip interactive title={tooltipText} placeholder='right'>
                  <div>
                    <IconV2 small name='info' color={publicaColors.mediumGray} />
                  </div>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const AutocompleteAndIncludeRow = (props: IInputRow, disableEdit: boolean, classes: any) => {
  const {
    // REQUIRED PARAMS (1 is textfield, 2 is inclusion / exclusion)
    title1,
    value1,
    handleChange1,
    value2,
    handleChange2,
    isActiveValue1,
    disabled1,
    // OPTIONAL PARAMS for inclusion/exclusion
    disabledvalues1,
    isInventoryRoutingPage,
    disabled2,
    tooltipText,
    placeholderText,
    optionalIcon,
    optionalIconInfo,
    marked,
    markedEnabled,
    handleChangeMarked,
    enableSmallRow,
    maxWidth,
  } = props;

  return (
    <Grid item className={classes.gridItemMidMargin}>
      <Grid container direction='row' justify='flex-start' alignItems='center' spacing={1}>
        {/* Title */}
        <Grid item xs={3}>
          <Grid container direction='row' justify='flex-start' alignItems='center' spacing={0}>
            {!!markedEnabled && handleChangeMarked && (
              <Grid item>
                <Checkbox
                  className={classes.checkbox}
                  checked={marked}
                  onChange={handleChangeMarked}
                />
              </Grid>
            )}
            <Grid item>
              <Typography
                className={
                  isActiveValue1() ? classes.insideAccordionTextBold : classes.insideAccordionText
                }
              >
                {title1}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        {/* Optional second icon */}
        {!enableSmallRow && (
          <Grid item xs={1}>
            {optionalIcon && optionalIconInfo && (
              <Tooltip title={optionalIconInfo}>
                <img src='/assets/shared/svg/strikethrough_flag.svg' />
              </Tooltip>
            )}
          </Grid>
        )}
        {/* targeting inclusion/exclusion */}
        {!enableSmallRow && (
          <Grid item xs={1}>
            {handleChange2 && (
              <ConfirmationDialogButton
                title='Targeting Change'
                description={
                  isInventoryRoutingPage
                    ? makeInventoryRouteTargetingChangeDescription(value2)
                    : makeTargetingChangeDescription(value2)
                }
                triggerComponentInfo={
                  isInventoryRoutingPage
                    ? makeInventoryRouteTargetingChangeInfoHover(value2)
                    : makeTargetingChangeInfoHover(value2)
                }
                acceptButtonTitle='Confirm'
                acceptButtonCallBack={handleChange2}
                disabled={disableEdit || disabled1 || disabled2}
                refuseButtonTitle='Cancel'
              >
                <IconButton>
                  {value2 ? (
                    <IconV2 name='checkmark-plain' large color={publicaColors.green} />
                  ) : (
                    <IconV2 name='unavailable' large color={publicaColors.red} />
                  )}
                </IconButton>
              </ConfirmationDialogButton>
            )}
          </Grid>
        )}
        {/* Textfield and info */}
        <Grid item xs={7}>
          <Grid container alignItems='center' justify='flex-start' direction='row' spacing={1}>
            <Grid item xs={10} style={{ maxWidth: maxWidth ? maxWidth : '' }}>
              <Autocomplete
                multiple
                freeSolo
                autoSelect
                options={[]}
                value={value1.length ? value1.split(',').filter((ele: any) => ele.length > 0) : []}
                onChange={(
                  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                  newVal,
                ) => {
                  e.target.value = newVal.filter((v) => v.length).join(',');
                  if (handleChange1) {
                    handleChange1(e);
                  }
                }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  if (target.value[target.value.length - 1] === ',') {
                    if (handleChange1) {
                      if (value1.length !== 0) {
                        target.value = [value1, target.value].join(',');
                      }
                      handleChange1(e);
                    }
                  }
                }}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      disabled={(disabledvalues1 || []).indexOf(option) !== -1} // not sure if [] is appropriate
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant='outlined'
                    label=''
                    placeholder={placeholderText || ''}
                  />
                )}
              />
            </Grid>
            {tooltipText && tooltipText !== '' && (
              <Grid item xs={2} className={classes.infoIconGrid}>
                <Tooltip interactive title={tooltipText} placeholder='right'>
                  <div>
                    <IconV2 small name='info' color={publicaColors.mediumGray} />
                  </div>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const SimpleSingleSelectRow = (props: IInputRow, disableEdit: boolean, classes: any) => {
  const {
    // REQUIRED PARAMS
    field1,
    title1,
    value1,
    handleChange1,
    simpleSelectItems,
    isActiveValue1,
    disabled1,
    // OPTIONAL PARAMS
    optionalIcon,
    optionalIconInfo,
    marked,
    markedEnabled,
    cloneEnabled,
    handleChangeMarked,
    disableCheck,
    enableSmallRow,
    maxWidth,
    tooltipText,
    hasButton,
    buttonOnClick,
    buttonText,
    isSubRow,
  } = props;
  return (
    <Grid item className={classes.gridItemSmallMargin}>
      <Grid container direction='row' justifyContent='flex-start' alignItems='center' spacing={1}>
        {/* Title */}
        <Grid item xs={3} justifyContent={!isSubRow ? 'flex-start' : 'flex-end'}>
          <Grid
            container
            direction='row'
            justifyContent={!isSubRow ? 'flex-start' : 'flex-end'}
            alignItems='center'
            spacing={0}
          >
            {(!!markedEnabled || !!cloneEnabled) && handleChangeMarked && (
              <Grid item>
                <Checkbox
                  className={classes.checkbox}
                  checked={marked}
                  onChange={handleChangeMarked}
                  disabled={disableCheck}
                />
              </Grid>
            )}
            <Grid item>
              <Text
                bold={isActiveValue1()}
                small={!isLanguageOversized(i18next.language)}
                extraSmall={isLanguageOversized(i18next.language)}
              >
                {title1}
              </Text>
            </Grid>
          </Grid>
        </Grid>
        {/* Optional second icon */}
        {!enableSmallRow && (
          <Grid item xs={1}>
            {optionalIcon && optionalIconInfo && (
              <Tooltip title={optionalIconInfo}>
                <img src='/assets/shared/svg/strikethrough_flag.svg' />
              </Tooltip>
            )}
          </Grid>
        )}
        {/* Empty grid for spacing */}
        {!enableSmallRow && <Grid item xs={1} />}
        {/* Dropdown */}
        <Grid
          item
          xs={
            (tooltipText && tooltipText !== '') ||
            (isSubRow && (!tooltipText || tooltipText === ''))
              ? 2
              : 6
          }
          style={{ maxWidth: maxWidth ? maxWidth : '' }}
        >
          <Select
            name={field1}
            className={props.resource ? `pb-bidder-settings-form-select-${props.resource}` : ''}
            disabled={disableEdit || disabled1}
            style={{
              fontSize: isLanguageOversized(i18next.language)
                ? fontSizing.extraSmall
                : fontSizing.small,
              minWidth: 100,
            }}
            value={value1}
            onChange={handleChange1}
          >
            {(simpleSelectItems || []).map((item) => {
              return <MenuItem value={item.key}>{item.text}</MenuItem>;
            })}
          </Select>
        </Grid>
        {tooltipText && tooltipText !== '' && (
          <Grid item className={classes.infoIconGrid}>
            <Tooltip interactive title={tooltipText} placeholder='right'>
              <div>
                <IconV2 small name='info' color={publicaColors.mediumGray} />
              </div>
            </Tooltip>
          </Grid>
        )}
        {/* Button */}
        {hasButton && buttonText !== '' && (
          <Grid item>
            <TextButton
              onClick={() => {
                if (buttonOnClick) {
                  buttonOnClick();
                }
              }}
            >
              {buttonText}
            </TextButton>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export const SimpleSwitchRow = (props: IInputRow, disableEdit: boolean, classes: any) => {
  const {
    // REQUIRED PARAMS
    field1,
    title1,
    value1,
    handleChange1,
    isActiveValue1,
    disabled1,
    // OPTIONAL PARAMS
    optionalIcon,
    optionalIconInfo,
    tooltipText,
    marked,
    markedEnabled,
    cloneEnabled,
    handleChangeMarked,
    disableCheck,
    enableSmallRow,
    helperText,
    maxWidth,
    singleLineSwitchText,
  } = props;
  return (
    <Grid item className={classes.gridItemSmallMargin}>
      <Grid container direction='row' justify='flex-start' alignItems='center' spacing={1}>
        {/* Title*/}
        <Grid item xs={singleLineSwitchText ? 6 : 3}>
          <Grid container direction='row' justify='flex-start' alignItems='center' spacing={0}>
            {(!!markedEnabled || !!cloneEnabled) && handleChangeMarked && (
              <Grid item>
                <Checkbox
                  className={classes.checkbox}
                  checked={marked}
                  onChange={handleChangeMarked}
                  disabled={disableCheck}
                />
              </Grid>
            )}
            <Grid item>
              <Typography
                className={
                  isActiveValue1() ? classes.insideAccordionTextBold : classes.insideAccordionText
                }
                style={{
                  ...(singleLineSwitchText ? { maxWidth: 'initial' } : {}),
                  color: isActiveValue1() ? publicaColors.veryDarkGray : publicaColors.gray,
                }}
              >
                {title1}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        {/* Optional second icon */}
        {!enableSmallRow && (
          <Grid item xs={1}>
            {optionalIcon && optionalIconInfo && (
              <Tooltip title={optionalIconInfo}>
                <img src='/assets/shared/svg/strikethrough_flag.svg' />
              </Tooltip>
            )}
          </Grid>
        )}
        {/* Empty grid for spacing */}
        {!enableSmallRow && <Grid item xs={1} />}
        {/* Switch */}
        <Grid item xs={singleLineSwitchText ? 3 : 6} style={{ maxWidth: maxWidth ? maxWidth : '' }}>
          <Grid container direction='row' alignItems='center'>
            <Grid item style={{ marginLeft: -10, fontSize: 14 }}>
              {singleLineSwitchText && (value1 ? 'Enabled' : 'Disabled')}
              <Switch
                disabled={disableEdit || disabled1}
                name={field1 ? field1 : removeSpaces(title1)}
                checked={value1}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  if (handleChange1) {
                    handleChange1(event.target.checked);
                  }
                }}
              />
            </Grid>
            {!!helperText && <span className={classes.spanActive}>{helperText}</span>}
            {tooltipText && tooltipText !== '' && (
              <Grid item className={classes.infoIconGrid}>
                <Tooltip interactive title={tooltipText} placeholder='right'>
                  <div>
                    <IconV2 small name='info' color={publicaColors.mediumGray} />
                  </div>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const SelectTextfieldRemove = (props: IInputRow, disableEdit: boolean, classes: any) => {
  const {
    idx,
    // SELECT
    title1,
    simpleSelectItems = [],
    value1,
    handleChange1,
    // TEXTFIELD
    // title2,
    value2,
    placeholderText,
    handleChange2,
    // REMOVE BUTTON
    handleClick1,
    // MORE PARAMS
    disabled1,
    // OPTIONAL PARAMS
    optionalIcon,
    optionalIconInfo,
    error,
    helperText,
    marked,
    markedEnabled,
    cloneEnabled,
    handleChangeMarked,
    disableCheck,
    enableSmallRow,
    maxWidth,
  } = props;
  if (!handleChange1) {
    return null;
  }
  return (
    <Grid item className={classes.gridItemSmallMargin} style={{ marginTop: 4 }}>
      <Grid
        container
        direction='row'
        justify='space-between'
        alignItems='center'
        spacing={1}
        style={{ backgroundColor: '#F8F8F8' }}
      >
        <Grid item xs={3}>
          <Grid container direction='row' justify='flex-start' alignItems='center' spacing={0}>
            {(!!markedEnabled || !!cloneEnabled) && handleChangeMarked && (
              <Grid item>
                <Checkbox
                  className={classes.checkbox}
                  checked={marked}
                  onChange={handleChangeMarked}
                  disabled={disableCheck}
                />
              </Grid>
            )}
            <Grid item style={{ width: '100%', paddingLeft: 16 }}>
              {/* Select */}
              <FormControl fullWidth>
                <Select
                  labelId={`${title1}-${idx}`}
                  id={`${title1}-${idx}`}
                  value={value1}
                  label='Device'
                  onChange={(e) => handleChange1({ e, idx })}
                  disabled={disableEdit}
                >
                  {simpleSelectItems.map((item) => (
                    <MenuItem value={item}>{item === 'Xbox' ? 'Microsoft or Xbox' : item}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        {/* Optional second icon */}
        {!enableSmallRow && (
          <Grid item xs={1}>
            {optionalIcon && optionalIconInfo && (
              <Tooltip title={optionalIconInfo}>
                <img src='/assets/shared/svg/strikethrough_flag.svg' />
              </Tooltip>
            )}
          </Grid>
        )}
        {/* Textfield */}
        <Grid item xs={7}>
          <Grid container alignItems='center' justify='flex-start' direction='row' spacing={1}>
            <Grid item xs={10} style={{ maxWidth: maxWidth || '' }} alignItems='center'>
              <Textarea
                fullWidth
                msDelay={800}
                placeholder={placeholderText || ''}
                isDisabled={disableEdit || disabled1}
                initialValue={value2 || ''}
                handleOnChange={(value: string) => {
                  if (handleChange2) handleChange2({ value, idx });
                }}
                style={{ marginTop: '4px' }}
              />
              {helperText && (
                <Text
                  extraSmall
                  style={{
                    color: error ? publicaColors.red : fontColorsDefault.muted,
                  }}
                >
                  {helperText}
                </Text>
              )}
            </Grid>
            <Grid item xs={2}>
              <Button
                onClick={() => {
                  if (handleClick1) handleClick1(idx);
                }}
                disabled={disableEdit}
              >
                <DeleteIcon style={{ color: publicaColors.mediumLightGray }} />
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const AddRowModelSecondaryButton = (
  props: IInputRow,
  disableEdit: boolean,
  classes: any,
) => {
  const { title1, handleClick1, disabled1 } = props;
  return (
    <Grid item className={classes.gridItemSmallMargin}>
      <ModelSecondaryButton
        variant='text'
        onClick={handleClick1}
        disabled={disableEdit || disabled1}
      >
        {title1}
      </ModelSecondaryButton>
    </Grid>
  );
};
